import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SEOHead from "./SEOHead";
import logo from "../assets/logo.jpg";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Support.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45 },
  }),
};

const CONTACT_CARDS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    title: "Email Support",
    description: "Send us a detailed message and our support team will get back to you within 24 hours.",
    cta: "Send Email",
    href: "mailto:support@comlinkr.com",
    color: "blue",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    title: "Live Chat",
    description: "Chat with a real person from our team. Available Monday–Friday, 9am–6pm GMT.",
    cta: "Start Chat",
    href: "mailto:support@comlinkr.com",
    color: "green",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
    title: "Report a Bug",
    description: "Found something broken? Help us fix it by sending a quick bug report with details.",
    cta: "Report Now",
    href: "mailto:support@comlinkr.com?subject=Bug%20Report",
    color: "orange",
  },
];

const SUBJECTS = [
  "General Inquiry",
  "Account & Profile",
  "Housing Feature",
  "Food & Restaurants",
  "Community & Feed",
  "Technical Issue / Bug",
  "Privacy & Data",
  "Other",
];

const RESPONSE_STATS = [
  { label: "Avg. Response Time", value: "< 24 hrs" },
  { label: "Support Hours", value: "Mon–Fri, 9am–6pm GMT" },
  { label: "Satisfaction Rate", value: "98%" },
];

const Support = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.subject) e.subject = "Please choose a subject";
    if (!form.message.trim()) e.message = "Message cannot be empty";
    return e;
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const subject = encodeURIComponent(`[${form.subject}] Message from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nSubject: ${form.subject}\n\n${form.message}`);
    window.location.href = `mailto:support@comlinkr.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/faq`);
  };

  return (
    <div className="support-page">
      <SEOHead
        title="Support & Help Center — ComLinkr"
        description="Need help with ComLinkr? Browse our help center, contact support, report a bug, or send us a message. We're here for you."
        canonical="https://www.comlinkr.com/support"
      />

      <button className="back-btn-support" onClick={() => navigate("/")}>← Back to Home</button>

      {/* ── HERO ── */}
      <section className="support-hero">
        <div className="container text-center">
          <motion.div
            className="support-logo-wrap"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, type: "spring" }}
          >
            <img src={logo} alt="ComLinkr" className="support-logo" />
          </motion.div>

          <motion.p
            className="support-tagline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            WE FIND FOR YOU
          </motion.p>

          <motion.h1
            className="support-hero-title"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            How can we <span className="support-gradient-text">help you?</span>
          </motion.h1>

          <motion.p
            className="support-hero-sub"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55 }}
          >
            Search our help articles, browse topics, or reach out to our team directly.
          </motion.p>

          <motion.form
            className="support-search-form"
            onSubmit={handleSearch}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.65 }}
            role="search"
          >
            <div className="support-search-bar">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="search"
                placeholder="Search help articles, FAQs, topics…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search help"
                className="support-search-input"
              />
              <button type="submit" className="search-submit-btn">Search</button>
            </div>
          </motion.form>

          <motion.div
            className="support-quick-links"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <span className="quick-links-label">Popular:</span>
            <button onClick={() => navigate("/faq")} className="quick-chip">FAQ</button>
            <button onClick={() => navigate("/privacy")} className="quick-chip">Privacy Policy</button>
            <button onClick={() => navigate("/delete-account")} className="quick-chip">Delete Account</button>
            <a href="mailto:support@comlinkr.com" className="quick-chip">Email Support</a>
          </motion.div>
        </div>
      </section>

      {/* ── RESPONSE STRIP ── */}
      <section className="response-strip">
        <div className="container">
          <div className="response-strip-inner">
            {RESPONSE_STATS.map((stat, i) => (
              <motion.div
                key={i}
                className="response-stat"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
              >
                <span className="response-value">{stat.value}</span>
                <span className="response-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT CARDS ── */}
      <section className="support-cards-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Get in Touch</span>
            <h2 className="section-title">Choose how to reach us</h2>
            <p className="section-sub">Multiple ways to connect — pick what works best for you.</p>
          </motion.div>

          <div className="support-cards-grid">
            {CONTACT_CARDS.map((card, i) => (
              <motion.div
                key={i}
                className={`support-card support-card--${card.color}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                whileHover={{ y: -6, transition: { duration: 0.22 } }}
              >
                <div className={`support-card-icon-wrap support-card-icon-wrap--${card.color}`} aria-hidden="true">
                  {card.icon}
                </div>
                <h3 className="support-card-title">{card.title}</h3>
                <p className="support-card-desc">{card.description}</p>
                <a href={card.href} className={`support-card-btn support-card-btn--${card.color}`}>
                  {card.cta}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <section className="support-form-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7">
              <motion.div
                className="support-form-card"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {submitted ? (
                  <motion.div
                    className="form-success"
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <div className="success-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <h3>Message sent!</h3>
                    <p>Thanks for reaching out, <strong>{form.name}</strong>. We'll get back to you at <strong>{form.email}</strong> within 24 hours.</p>
                    <button className="btn-gradient" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }}>
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <div className="form-header text-center">
                      <span className="section-eyebrow">Direct Contact</span>
                      <h2 className="section-title">Send us a message</h2>
                      <p className="section-sub">Fill in the form below and we'll respond as soon as possible.</p>
                    </div>

                    <form onSubmit={handleSubmit} noValidate>
                      <div className="form-row-2">
                        <div className="form-group">
                          <label htmlFor="support-name" className="form-label">Full Name</label>
                          <input
                            id="support-name"
                            name="name"
                            type="text"
                            className={`support-input${errors.name ? " input-error" : ""}`}
                            placeholder="Jane Smith"
                            value={form.name}
                            onChange={handleChange}
                            autoComplete="name"
                          />
                          {errors.name && <span className="field-error">{errors.name}</span>}
                        </div>

                        <div className="form-group">
                          <label htmlFor="support-email" className="form-label">Email Address</label>
                          <input
                            id="support-email"
                            name="email"
                            type="email"
                            className={`support-input${errors.email ? " input-error" : ""}`}
                            placeholder="jane@example.com"
                            value={form.email}
                            onChange={handleChange}
                            autoComplete="email"
                          />
                          {errors.email && <span className="field-error">{errors.email}</span>}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="support-subject" className="form-label">Subject</label>
                        <div className="select-wrap">
                          <select
                            id="support-subject"
                            name="subject"
                            className={`support-input support-select${errors.subject ? " input-error" : ""}`}
                            value={form.subject}
                            onChange={handleChange}
                          >
                            <option value="">Choose a topic…</option>
                            {SUBJECTS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <svg className="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                        {errors.subject && <span className="field-error">{errors.subject}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="support-message" className="form-label">Message</label>
                        <textarea
                          id="support-message"
                          name="message"
                          className={`support-input support-textarea${errors.message ? " input-error" : ""}`}
                          placeholder="Describe your issue or question in detail…"
                          rows={5}
                          value={form.message}
                          onChange={handleChange}
                        />
                        {errors.message && <span className="field-error">{errors.message}</span>}
                        <span className="char-count">{form.message.length} / 2000</span>
                      </div>

                      <button type="submit" className="btn-gradient submit-btn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        Send Message
                      </button>

                      <p className="form-note">
                        By submitting this form, you agree to our{" "}
                        <a href="/privacy" className="form-link">Privacy Policy</a>.
                      </p>
                    </form>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TOPICS ── */}
      <section className="support-topics-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Browse Topics</span>
            <h2 className="section-title">Help by category</h2>
          </motion.div>
          <div className="topics-grid">
            {[
              { icon: "🏠", label: "Housing", desc: "Listings, roommates, matching" },
              { icon: "🍜", label: "Food & Restaurants", desc: "Discovery, cuisine filters" },
              { icon: "🌐", label: "Community", desc: "Q&A, topics, connections" },
              { icon: "📰", label: "Feed & Events", desc: "News, local events, stories" },
              { icon: "🔐", label: "Account & Privacy", desc: "Settings, data, deletion" },
              { icon: "⚙️", label: "Technical Support", desc: "Bugs, errors, performance" },
            ].map((t, i) => (
              <motion.a
                key={i}
                href="/faq"
                className="topic-chip"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                whileHover={{ y: -4 }}
              >
                <span className="topic-icon" aria-hidden="true">{t.icon}</span>
                <div>
                  <span className="topic-label">{t.label}</span>
                  <span className="topic-desc">{t.desc}</span>
                </div>
                <svg className="topic-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Support;
